import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { ApiService } from '@services/api.service';
import {
  ManagementCombinedSubcostModel,
  ManagementContainerCostModel,
  ManagementCreateCombinedPriceListModel,
  ManagementPriceListCombinedDetailedModel,
  ManagementPriceListCombinedSearchModel,
  ManagementUpdatePriceListCombinedModel,
} from '@data-layer/models/management';
import { PaginatedResultModel } from '@data-layer/models/common';

@Injectable({
  providedIn: 'root',
})
export class PriceListManagementRepository {
  constructor(private apiService: ApiService) {}

  public getActivePriceLists(
    originPortId: number,
    destinationPortId: number,
    effectiveDate: string,
  ): Observable<ManagementPriceListCombinedDetailedModel[]> {
    return this.apiService.get<ManagementPriceListCombinedDetailedModel[]>(
      `${environment.url.freight.combinedPriceList}/active`,
      { params: { originPortId: originPortId, destinationPortId: destinationPortId, effectiveDate: effectiveDate } },
      { isShowLoader: false },
    );
  }

  public getPriceLists(
    params?: ManagementPriceListCombinedSearchModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<ManagementPriceListCombinedDetailedModel>> {
    return this.apiService.get<PaginatedResultModel<ManagementPriceListCombinedDetailedModel>>(
      `${environment.url.freight.combinedPriceList}`,
      { params },
      { isShowLoader },
    );
  }

  public getSubCosts(): Observable<Array<{ name: string; id: string }>> {
    return this.apiService.get<Array<{ name: string; id: string }>>(`${environment.url.freight.subcostNames}`, null, {
      isShowLoader: false,
    });
  }

  public addSubCost(name: string): Observable<{ name: string; id: string }> {
    return this.apiService.post<{ name: string; id: string }>(`${environment.url.freight.subcostNames}`, `"${name}"`, null, {
      isShowLoader: false,
    });
  }

  public get(id: number, isShowLoader?: boolean): Observable<ManagementCreateCombinedPriceListModel> {
    return this.apiService.get<ManagementCreateCombinedPriceListModel>(`${environment.url.freight.combinedPriceList}/${id}`, null, {
      isShowLoader,
    });
  }

  public create(data: ManagementCreateCombinedPriceListModel, isShowLoader?: boolean): Observable<ManagementCreateCombinedPriceListModel> {
    return this.apiService.post<ManagementCreateCombinedPriceListModel>(`${environment.url.freight.combinedPriceList}`, data, null, {
      isShowLoader,
    });
  }

  public duplicate(
    data: ManagementCreateCombinedPriceListModel,
    pricelistId: string,
    isShowLoader?: boolean,
  ): Observable<ManagementCreateCombinedPriceListModel> {
    return this.apiService.post<ManagementCreateCombinedPriceListModel>(
      `${environment.url.freight.combinedPriceList}/${pricelistId}/duplicate`,
      data,
      null,
      {
        isShowLoader,
      },
    );
  }

  public createSubcost(
    priceListId: number,
    data: ManagementCombinedSubcostModel,
    isShowLoader?: boolean,
  ): Observable<ManagementCombinedSubcostModel> {
    return this.apiService.post<ManagementCombinedSubcostModel>(
      `${environment.url.freight.combinedPriceList}/${priceListId}/subcosts`,
      data,
      null,
      {
        isShowLoader,
      },
    );
  }

  public updatePriceList(
    priceListId: number,
    data: ManagementUpdatePriceListCombinedModel,
    isShowLoader?: boolean,
  ): Observable<ManagementPriceListCombinedDetailedModel> {
    return this.apiService.put<ManagementPriceListCombinedDetailedModel>(
      `${environment.url.freight.combinedPriceList}/${priceListId}`,
      data,
      null,
      {
        isShowLoader,
      },
    );
  }

  public getContainerCosts(priceListId: number, isShowLoader?: boolean): Observable<ManagementContainerCostModel[]> {
    return this.apiService.get<ManagementContainerCostModel[]>(
      `${environment.url.freight.combinedPriceList}/${priceListId}/container-costs`,
      null,
      {
        isShowLoader,
      },
    );
  }
}
